import { deleteDB, openDB } from 'idb';
import { z } from 'zod';

import type { ModuleProgress, Profile, UserState } from './user';
import { useUserStore } from './user';

/**
 * One-shot import of the previous version's IndexedDB, so a learner who used
 * the Next.js app keeps their name, buddy and completed modules at cutover.
 *
 * Dexie itself is not a dependency here: the old database is read through the
 * raw IndexedDB API (via `idb`) and then deleted, which is all a one-way
 * migration needs.
 */

/** Database and table names from the old app's `src/db.ts`. */
const LEGACY_DB = 'BeachPassportDatabase';
const USER_STORE = 'user';
const SCORE_STORE = 'topicScore';

/** The old `UserProfile` row. Codes were upper-case and the field was `age`. */
const legacyUserSchema = z.object({
  language: z.string(),
  age: z.enum(['child', 'teen', 'adult']),
  name: z.string().optional(),
  character: z.enum(['duck', 'kangaroo', 'koala', 'squirrel']).optional(),
  completed: z.boolean().optional(),
  certificateId: z.string().optional(),
  isAustralian: z.boolean().optional(),
});

/** The old `TopicScore` row. `id` is the topic UID, not a `module_key`. */
const legacyScoreSchema = z.object({
  id: z.string(),
  nQuestions: z.number(),
  nQuestionsCompleted: z.number(),
  nQuestionsAnswered: z.number(),
  nQuestionsTried: z.number(),
  lastScore: z.number().optional(),
});

/** How a topic UID maps to the stable analytics key the new store is keyed by. */
export type TopicKey = { uid: string; moduleKey: string };

/** What the migration writes: the persisted slices, and nothing else. */
export type LegacyImport = Pick<UserState, 'profile' | 'modules'>;

/**
 * Maps the old app's two tables onto the new store. Pure, and defensive: the
 * rows come from a database this app never wrote, so anything unparseable is
 * skipped rather than trusted. Returns `null` when there is no usable user,
 * because a profile is what the guards key off.
 */
export function mapLegacy(user: unknown, scores: unknown, topics: Array<TopicKey>): LegacyImport | null {
  const parsedUser = legacyUserSchema.safeParse(user);

  if (!parsedUser.success) {
    return null;
  }

  const { language, age, name, character, completed, certificateId, isAustralian } = parsedUser.data;

  const profile: Profile = {
    language: language.toLowerCase(),
    audience: age,
    buddy: character ?? 'kangaroo',
    completed: completed ?? false,
    ...(name === undefined || name === '' ? {} : { name }),
    ...(certificateId === undefined ? {} : { certificateId }),
    ...(isAustralian === undefined ? {} : { isAustralian }),
  };

  const moduleKeyByUid = new Map(topics.map((topic) => [topic.uid, topic.moduleKey]));
  const modules: Record<string, ModuleProgress> = {};

  for (const row of Array.isArray(scores) ? scores : []) {
    const parsed = legacyScoreSchema.safeParse(row);

    if (!parsed.success) {
      continue;
    }

    const { id, nQuestions, nQuestionsCompleted, nQuestionsAnswered, nQuestionsTried, lastScore } = parsed.data;

    // A score for a topic the new app cannot see is dropped: without a
    // `module_key` it could never be matched to a module again.
    const moduleKey = moduleKeyByUid.get(id);

    if (moduleKey === undefined) {
      continue;
    }

    modules[moduleKey] = {
      nSlices: nQuestions,
      nCompleted: nQuestionsCompleted,
      nAnswered: nQuestionsAnswered,
      nTried: nQuestionsTried,
      ...(lastScore === undefined ? {} : { bestScore: lastScore }),
    };
  }

  return { profile, modules };
}

/**
 * Reads the legacy database once, writes it into the store and deletes it.
 * Resolves `true` only when a profile was actually imported.
 *
 * Client-only, and guarded twice: the `migrated` flag means it runs at most
 * once per device, and every failure is swallowed so a corrupt or absent
 * database simply leaves the store empty and the learner onboards normally.
 */
export async function importLegacyOnce(topics: Array<TopicKey>): Promise<boolean> {
  if (typeof indexedDB === 'undefined' || useUserStore.getState().migrated) {
    return false;
  }

  try {
    // No version and no upgrade callback: opening at the current version means
    // this never migrates the old schema, only reads it.
    const db = await openDB(LEGACY_DB);

    const hasTables = db.objectStoreNames.contains(USER_STORE);

    const [users, scores] = hasTables
      ? await Promise.all([
          db.getAll(USER_STORE),
          db.objectStoreNames.contains(SCORE_STORE) ? db.getAll(SCORE_STORE) : Promise.resolve([]),
        ])
      : [[], []];

    db.close();

    // `useUser` treated the first row as *the* user, so this does too.
    const imported = mapLegacy(users[0], scores, topics);

    if (imported) {
      useUserStore.setState({ ...imported, migrated: true });
    } else {
      useUserStore.setState({ migrated: true });
    }

    await deleteDB(LEGACY_DB);

    return imported !== null;
  } catch {
    // Mark it done anyway: a database that cannot be read now will not read
    // any better on the next page load, and retrying would cost every visit.
    useUserStore.setState({ migrated: true });

    return false;
  }
}
